import React from 'react';
import {
  FiHome,
  FiGlobe, 
  FiUser,
  FiMail,
  FiPhone,
  FiCreditCard,
  FiCheck,
  FiFileText,
  FiUsers,
  FiMapPin,
  FiLayers
} from 'react-icons/fi';

export function ReviewStep({ data }) {
  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Business Details - Expanded to include new fields */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={FiMapPin} label="TNS Region" value={formatRegionName(data?.region) || 'Not specified'} />
            <InfoRow icon={FiGlobe} label="Country" value={data?.country || 'Not specified'} />
            <InfoRow icon={FiLayers} label="Business Sector" value={formatBusinessSector(data?.businessSector) || 'Not specified'} />
            <InfoRow icon={FiCreditCard} label="Expected Monthly Volume" value={data?.expectedMonthlyVolume || 'Not specified'} />
          </div>
          
          {/* Optional Business Info */}
          {(data?.businessName || data?.website || data?.contactName || data?.contactEmail || data?.contactPhone) && (
            <>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-900 mb-3">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data?.businessName && <InfoRow icon={FiHome} label="Business Name" value={data.businessName} />}
                  {data?.website && <InfoRow icon={FiGlobe} label="Website" value={data.website} />}
                  {data?.contactName && <InfoRow icon={FiUser} label="Contact Name" value={data.contactName} />}
                  {data?.contactEmail && <InfoRow icon={FiMail} label="Contact Email" value={data.contactEmail} />}
                  {data?.contactPhone && <InfoRow icon={FiPhone} label="Contact Phone" value={data.contactPhone} />}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Opportunity Background */}
      {data?.opportunityBackground && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-2">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
              <FiFileText className="w-5 h-5 mr-2 text-blue-600" />
              Opportunity Background
            </h3>
            <div className=" rounded-lg p-2 border border-blue-100">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{data.opportunityBackground}</p>
            </div>
          </div>
        </div>
      )}

      {/* Current Acquirers/Processors */}
      {data?.currentAcquirers && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-2">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
              <FiUsers className="w-5 h-5 mr-2 text-blue-600" />
              Current Acquirers/Processors
            </h3>
            <div className=" rounded-lg p-4 border border-orange-100">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{data.currentAcquirers}</p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Requirements - Multi-column grid layout */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Requirements</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            <Section
              title="Sales Channels"
              items={data?.channels?.map(formatLabel) || []}
            />
            <Section
              title="Payment Methods"
              items={data?.paymentMethods?.map(formatLabel) || []}
            />
            {data?.cardSchemes?.length > 0 && (
              <Section
                title="Card Schemes"
                items={data.cardSchemes.map(formatLabel)}
              />
            )}
            {data?.wallets?.length > 0 && (
              <Section title="Digital Wallets" items={data.wallets.map(formatLabel)} />
            )}
            {data?.terminalTypes?.length > 0 && (
              <Section
                title="Terminal Requirements"
                items={data.terminalTypes.map(formatLabel)}
              />
            )}
            {data?.ecommerceFeatures?.length > 0 && (
              <Section
                title="E-commerce Features"
                items={data.ecommerceFeatures.map(formatLabel)}
              />
            )}
            {data?.bnplProviders?.length > 0 && (
              <Section
                title="BNPL Providers"
                items={data.bnplProviders.map(formatLabel)}
              />
            )}
            <Section
              title="Integration Types"
              items={data?.integrationTypes?.map(formatLabel) || []}
            />
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      {data?.notes && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{data.notes}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        <div className="text-sm text-gray-600 truncate">{value}</div>
      </div>
    </div>
  );
}

function Section({ title, items }) {
  if (!items || items.length === 0) return null;
  
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">{title}</h4>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-start space-x-2 text-sm">
            <FiCheck className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
            <span className="text-gray-700 leading-relaxed">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatLabel(str) {
  return str
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatRegionName(region) {
  const regionNames = {
    'usa': 'Region 1 - United States',
    'eu': 'Region 2 - UK, Ireland and MLE',
    'apac': 'Region 3 - APAC',
    'region4': 'Region 4 - Brazil'
  };
  return regionNames[region] || region;
}

function formatBusinessSector(sector) {
  const sectorNames = {
    'retail': 'Retail',
    'fuel': 'Fuel & Convenience',
    'parking': 'Parking',
    'vending': 'Vending',
    'ev': 'EV Charging',
    'hospitality': 'Hospitality',
    'ticketing and entertainment': 'Ticketing & Entertainment',
    'transportation': 'Transportation',
    'unattended other': 'Unattended (other)',
    'other': 'Other'
  };
  return sectorNames[sector] || sector;
}

// Demo data to show the improved layout
const demoData = {
  region: "usa",
  country: "United States",
  businessSector: "retail",
  expectedMonthlyVolume: "50,000 transactions",
  opportunityBackground: "This is a major retail chain looking to upgrade their payment infrastructure across 200+ locations. They are currently running on legacy systems and need a modern, unified payment solution that can handle high volumes during peak shopping periods.",
  currentAcquirers: "Currently using FirstData for card processing and PayPal for online payments. Looking to consolidate to a single provider due to high fees and poor reporting capabilities. Contract expires in Q3 2024.",
  businessName: "Acme Retail Corp",
  website: "https://acmeretail.com",
  contactName: "John Smith",
  contactEmail: "john.smith@acmeretail.com",
  contactPhone: "+1-555-0123",
  channels: ["online", "inStore", "mobile"],
  paymentMethods: ["creditCard", "debitCard", "digitalWallet"],
  cardSchemes: ["visa", "mastercard", "amex"],
  wallets: ["applePay", "googlePay", "paypal"],
  terminalTypes: ["countertop", "mobile", "integrated"],
  ecommerceFeatures: ["oneClick", "subscriptions", "multiCurrency"],
  bnplProviders: ["klarna", "afterpay"],
  integrationTypes: ["api", "sdk", "plugin"],
  notes: "Looking for a comprehensive payment solution that can handle high transaction volumes during peak shopping seasons. Priority on fast settlement times and detailed reporting capabilities."
};

export default function Demo() {
  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <ReviewStep data={demoData} />
    </div>
  );
}